import axios from 'axios';
import { jwtDecode } from 'jwt-decode'; // Fix the import for jwtDecode

class APIHandler {
    constructor() {
        this.api = axios.create({
            baseURL: '/api', // Relative path — works on any domain (gam.lec.li, gam-dev.lec.li, localhost)
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    handleLogout = () => {
        localStorage.removeItem('token');  // Remove the JWT token from localStorage
        localStorage.removeItem('refresh_token');  // Remove the refresh token
        window.location.href = '/';  // Redirect to login page
    };



    // Method to retrieve access token
    getToken() {
        return localStorage.getItem('token');
    }

    // Method to retrieve refresh token
    getRefreshToken() {
        return localStorage.getItem('refresh_token');
    }

    // Method to check if token is expired
    isTokenExpired(token) {
        try {
            const decoded = jwtDecode(token);
            return decoded.exp < Date.now() / 1000;
        } catch (error) {
            return true; // If any error occurs in decoding, treat the token as expired
        }
    }

    async copyUsersFromGroup(sourceGroupEmail, destinationGroupEmail) {
        return this.makeRequest('post', '/copy-users-from-group', {
            source_group_name: sourceGroupEmail,
            destination_group_name: destinationGroupEmail,
        });
    }

    async login(username, password) {
        try {
            const response = await this.api.post('/login', { username, password });
            localStorage.setItem('token', response.data.access_token);
            localStorage.setItem('refresh_token', response.data.refresh_token);
            return response.data;
        } catch (error) {
            console.error('Error logging in:', error);
            if (error.response && error.response.status === 401) {
                throw new Error('Invalid username or password');
            } else {
                throw new Error('An error occurred during login. Please try again later.');
            }
        }
    }

    // Method to refresh token
    async refreshToken() {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        try {
            const response = await this.api.post('/refresh-token', {
                refresh_token: refreshToken,
            });
            localStorage.setItem('token', response.data.access_token); // Save the new access token
            return response.data.access_token;
        } catch (error) {
            console.error('Error refreshing token:', error);
            return null;
        }
    }

    async chat(message, history = []) {
        return this.makeRequest('post', '/chat', { message, history });
    }

    // Wrapper method to make API requests, regenerating token if necessary
    async makeRequest(method, url, data = null) {
        let token = this.getToken();

        // If token is expired, attempt to refresh
        if (this.isTokenExpired(token)) {
            token = await this.refreshToken();
            if (!token) {
                throw new Error('Token refresh failed');
            }
        }

        try {
            const response = await this.api({
                method,
                url,
                data,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            console.error(`Error making API call to ${url}:`, error);
            throw error;
        }
    }

    async login(username, password) {
        try {
            const response = await this.api.post('/login', { username, password });
            localStorage.setItem('token', response.data.access_token);
            localStorage.setItem('refresh_token', response.data.refresh_token);
            return response.data;
        } catch (error) {
            console.error('Error logging in:', error);
            if (error.response && error.response.status === 401) {
                throw new Error('Invalid username or password');
            } else {
                throw new Error('An error occurred during login. Please try again later.');
            }
        }
    }

    // Fetch students for a specific class by classId
    async fetchGroupMembers(classId) {
        console.log(classId);
        return this.makeRequest('post', `/get-students`, { class_id: classId });
    }


    // Add a new student with name to the class
    async addMembers(classId, users) {
        return this.makeRequest('post', '/add-students', {
            class_id: classId,
            students: users // Send array of users in one request
        });
    }


    // Fetch users from a specific group by group name
    async fetchGroupUsers(groupName) {
        return this.makeRequest('post', '/get-users-in-group', { group_name: groupName });
    }

    async addEnabledStudentsToGroup(groupEmail, enabledStudents) {
        // Ensure the payload includes the email and timeout_duration for each student
        return this.makeRequest('post', '/add-users-to-group', {
            group_name: groupEmail,
            users: enabledStudents.map(student => ({
                email: student.email,
                timeout_duration: student.timeout_duration // Pass timeout duration for each student
            }))
        });
    }


    // Remove a student from the group
    async removeStudentFromGroup(groupEmail, studentEmail) {
        return this.makeRequest('post', '/remove-student-from-group', {
            group_name: groupEmail,
            student_email: studentEmail
        });
    }

    // Clear all users from the group
    async clearGroupUsers(groupEmail) {
        return this.makeRequest('post', '/clear-group-users', { group_name: groupEmail });
    }


    async searchUsersByName(name) {
        return this.makeRequest('post', '/search-users-by-name', { query: name });
    }

    async getUsersInGroup(groupName) {
        return this.makeRequest('post', '/get-users-in-group', { group_name: groupName });
    }

    async deleteStudent(classId, studentEmail) {
        return this.makeRequest('post', '/delete-student', {
            class_id: classId,
            student_email: studentEmail
        });
    }


    // Network management API methods
    async addNetwork(networkName, ssid, passcode) {
        return this.makeRequest('post', '/add-network', { network_name: networkName, ssid, passcode });
    }

    async getNetworks() {
        return this.makeRequest('get', '/get-networks');
    }

    async editNetwork(networkName, ssid, passcode) {
        return this.makeRequest('post', '/edit-network', { network_name: networkName, ssid, passcode });
    }

    async deleteNetwork(networkName) {
        return this.makeRequest('post', '/delete-network', { network_name: networkName });
    }

}

export default new APIHandler();
